import { Component, inject, signal } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs';

export interface Crumb {
  label: string;
  path: string | null;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.html',
  styleUrl: './breadcrumb.css',
})
export class BreadcrumbComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly crumbs = signal<Crumb[]>([]);

  constructor() {
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.buildCrumbs(this.route.root)),
    ).subscribe((crumbs) => this.crumbs.set(crumbs));

    // Build crumbs for the current route on init (in case NavigationEnd already fired)
    this.crumbs.set(this.buildCrumbs(this.route.root));
  }

  private buildCrumbs(route: ActivatedRoute, path: string = '', crumbs: Crumb[] = []): Crumb[] {
    const children = route.children;
    if (children.length === 0) {
      return crumbs;
    }

    for (const child of children) {
      const segments = child.snapshot.url.map((s) => s.path);
      if (segments.length === 0) {
        return this.buildCrumbs(child, path, crumbs);
      }

      const childPath = `${path}/${segments.join('/')}`;
      const label = child.snapshot.data['breadcrumb'] as string | undefined;
      if (label) {
        crumbs.push({ label, path: childPath });
      }
      return this.buildCrumbs(child, childPath, crumbs);
    }

    return crumbs;
  }

  protected navigate(crumb: Crumb): void {
    if (crumb.path) {
      this.router.navigate([crumb.path]);
    }
  }
}
